import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Home, ArrowLeft, Search } from 'lucide-react';

/**
 * 404 Not Found Page
 * Handles legacy WordPress URLs and non-existent pages
 * Sets proper canonical to homepage to avoid duplicate content issues
 */
const NotFound = () => {
  useEffect(() => {
    // Set page title
    document.title = "Page Not Found | 1st Ave ICP";
    
    // Set canonical to homepage to consolidate SEO
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute('href', 'https://1staveicp.com/');
    }
    
    // Set meta robots to noindex this page
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', 'noindex, follow');
    }
    
    // Cleanup on unmount
    return () => {
      if (robotsTag) {
        robotsTag.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
      }
    };
  }, []);

  return (
    <div 
      className="min-h-screen pt-32 pb-16 px-4"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      <div className="max-w-2xl mx-auto text-center">
        {/* Ice cream cone illustration */}
        <div className="mb-8">
          <img 
            src="/images/hero/hero-cone.png" 
            alt="Ice cream cone" 
            className="w-48 h-auto mx-auto opacity-50 transform rotate-12"
          />
        </div>
        
        {/* 404 Text */}
        <h1 
          className="text-8xl md:text-9xl font-black text-[#E91E8C] mb-4"
          style={{ fontFamily: 'Rowdies, cursive' }}
        >
          404
        </h1>
        
        <h2 
          className="text-2xl md:text-3xl font-bold text-gray-800 mb-4"
          style={{ fontFamily: 'Rowdies, cursive' }}
        >
          Oops! This Page Melted Away
        </h2>
        
        <p className="text-gray-600 mb-8 text-lg">
          The page you're looking for doesn't exist or has been moved. 
          Don't worry - our ice cream is still here!
        </p>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link to="/">
            <Button className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-8 py-4 rounded-full shadow-lg flex items-center gap-2">
              <Home className="w-5 h-5" />
              Go Home
            </Button>
          </Link>
          
          <Link to="/menu">
            <Button variant="outline" className="border-2 border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white font-bold px-8 py-4 rounded-full flex items-center gap-2">
              <Search className="w-5 h-5" />
              View Menu
            </Button>
          </Link>
        </div>
        
        {/* Back Link */}
        <button 
          onClick={() => window.history.back()}
          className="mt-8 text-gray-500 hover:text-[#E91E8C] flex items-center gap-2 mx-auto transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Go Back
        </button>
        
        {/* Popular Links */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500 mb-4">Popular pages:</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/menu" className="text-[#E91E8C] hover:underline">Menu</Link>
            <span className="text-gray-300">•</span>
            <Link to="/specials" className="text-[#E91E8C] hover:underline">Specials</Link>
            <span className="text-gray-300">•</span>
            <Link to="/book-event" className="text-[#E91E8C] hover:underline">Book Event</Link>
            <span className="text-gray-300">•</span>
            <Link to="/contact" className="text-[#E91E8C] hover:underline">Contact</Link>
            <span className="text-gray-300">•</span>
            <Link to="/find-us" className="text-[#E91E8C] hover:underline">Find Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
